import { useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import Image from '@tiptap/extension-image';
import StarterKit from '@tiptap/starter-kit';
import { EmojiButton } from '../comments/CommentInsertPickers';
import { isSafeLinkHref, normalizeLinkHref } from '../comments/commentKeymap';
import { LINK_OPTIONS, NonInclusiveLink } from '../notes/linkOptions';
import { useHuddleDictation } from './useHuddleDictation';
import { HuddleSpeakButton, HuddleSpeakFeedback } from './HuddleSpeak';

const IMAGE_TYPES = 'image/png,image/jpeg,image/webp,image/gif';

export interface HuddleCommentEditorProps {
  ref?: Ref<{ stopDictation: () => void }>;
  value: string;
  onChange: (html: string) => void;
  onUpload: (file: File) => Promise<string>;
  onUploadError: (cause: unknown) => void;
  disabled?: boolean;
}

/**
 * The public huddle's deliberately small comment editor.
 *
 * It writes the same bold/italic/list/link/image HTML that Hub comments use,
 * but carries no mention extension and offers no edit or delete path.
 */
export function HuddleCommentEditor({
  ref,
  value,
  onChange,
  onUpload,
  onUploadError,
  disabled = false,
}: HuddleCommentEditorProps) {
  const uploadRef = useRef(onUpload);
  uploadRef.current = onUpload;
  const uploadErrorRef = useRef(onUploadError);
  uploadErrorRef.current = onUploadError;
  const inputRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<string | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: false,
          horizontalRule: false,
          link: false,
          code: false,
          codeBlock: false,
          orderedList: false,
          blockquote: false,
          strike: false,
        }),
        NonInclusiveLink.configure({
          ...LINK_OPTIONS,
          isAllowedUri: (url: string) => isSafeLinkHref(url),
        }),
        Image.configure({ inline: false, allowBase64: false }),
      ],
      content: value,
      editable: !disabled,
      editorProps: {
        attributes: { class: 'comment-editor-surface', 'aria-label': 'Comment as Alex' },
        handlePaste: (_view, event) => {
          const file = Array.from(event.clipboardData?.files ?? []).find((item) =>
            item.type.startsWith('image/'),
          );
          if (file === undefined) return false;
          event.preventDefault();
          void insertUpload(file);
          return true;
        },
      },
      onUpdate: ({ editor: instance }) => onChange(instance.getHTML()),
    },
    [],
  );
  const editorRef = useRef(editor);
  editorRef.current = editor;
  const dictation = useHuddleDictation(editor, disabled);
  useImperativeHandle(ref, () => ({ stopDictation: dictation.cancel }));

  async function insertUpload(file: File): Promise<void> {
    const preview = URL.createObjectURL(file);
    if (previewRef.current !== null) URL.revokeObjectURL(previewRef.current);
    previewRef.current = preview;
    setUploadPreview(preview);
    try {
      const src = await uploadRef.current(file);
      editorRef.current?.chain().focus().setImage({ src, alt: 'Pasted screenshot' }).run();
    } catch (cause) {
      uploadErrorRef.current(cause);
    } finally {
      URL.revokeObjectURL(preview);
      if (previewRef.current === preview) {
        previewRef.current = null;
        setUploadPreview(null);
      }
    }
  }

  useEffect(
    () => () => {
      if (previewRef.current !== null) URL.revokeObjectURL(previewRef.current);
    },
    [],
  );

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (editor !== null && value === '' && !editor.isEmpty) {
      editor.commands.clearContent();
    }
  }, [editor, value]);

  if (editor === null) {
    return <div className="comment-editor-loading">Loading…</div>;
  }

  return (
    <div className="comment-editor">
      <div className="comment-editor-toolbar" role="toolbar" aria-label="Comment formatting">
        <button
          type="button"
          className={`comment-tool${editor.isActive('bold') ? ' comment-tool--on' : ''}`}
          aria-label="Bold"
          aria-pressed={editor.isActive('bold')}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          B
        </button>
        <button
          type="button"
          className={`comment-tool${editor.isActive('italic') ? ' comment-tool--on' : ''}`}
          aria-label="Italic"
          aria-pressed={editor.isActive('italic')}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          I
        </button>
        <button
          type="button"
          className={`comment-tool${editor.isActive('bulletList') ? ' comment-tool--on' : ''}`}
          aria-label="Bulleted list"
          aria-pressed={editor.isActive('bulletList')}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          •
        </button>
        <button
          type="button"
          className="comment-tool"
          aria-label="Link"
          disabled={disabled}
          onClick={() => {
            const typed = window.prompt('Link address');
            if (typed === null) return;
            const href = normalizeLinkHref(typed);
            if (!isSafeLinkHref(href)) {
              editor.chain().focus().unsetLink().run();
              return;
            }
            editor.chain().focus().setLink({ href }).run();
          }}
        >
          🔗
        </button>
        <button
          type="button"
          className="comment-tool"
          aria-label="Add screenshot"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          📷
        </button>
        <input
          ref={inputRef}
          className="huddle-upload-input"
          type="file"
          accept={IMAGE_TYPES}
          aria-label="Choose screenshot"
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            event.currentTarget.value = '';
            if (file !== undefined) void insertUpload(file);
          }}
        />
        {/* The Hub comment box's own emoji button (Kyu, 1 Oct, D8a). A text
            node, as in CommentEditor: a string would be parsed as HTML. */}
        <EmojiButton
          disabled={disabled}
          onPick={(emoji) =>
            editor.chain().focus().insertContent({ type: 'text', text: emoji }).run()
          }
        />
        <HuddleSpeakButton dictation={dictation} label="Dictate a comment" disabled={disabled} />
      </div>
      <EditorContent editor={editor} />
      <HuddleSpeakFeedback dictation={dictation} />
      {uploadPreview === null ? null : (
        <div className="huddle-upload-preview" role="status" aria-label="Screenshot uploading">
          <img src={uploadPreview} alt="Screenshot preview" />
          <span>Uploading…</span>
        </div>
      )}
    </div>
  );
}
